import {
  type ChatMessage,
  ChatThread,
  Composer,
  chatWhen,
  JumpToLatest,
  outgoingMessages,
  quoteOf,
  useStickToBottom,
} from '@snitch/ui/chat'
import { useDrafts } from '@snitch/ui/lib/media-send'
import { MediaViewer, type ViewerItem } from '@snitch/ui/media-viewer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useCanGoBack, useRouter } from '@tanstack/react-router'
import { ArrowLeft, Headset, LoaderCircle, Sparkles } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SupportFeedback } from '../../../components/support-feedback.tsx'
import { orpc } from '../../../lib/api.ts'
import { conversationQuery, unreadQuery } from '../../../lib/support.ts'
import { useSupportOutbox } from '../../../lib/support-outbox.tsx'
import { openExternalLink } from '../../../lib/telegram.ts'

export const Route = createFileRoute('/_authed/_shell/support')({
  component: SupportPage,
})

const TOPICS = ['Не подключается', 'Оплата и тариф', 'Устройства', 'Другой вопрос']

/** "Поддержка · Первый вход / Переписка / Отправка вложений": the chat with the team. */
function SupportPage() {
  const queryClient = useQueryClient()
  const conversation = useQuery({ ...conversationQuery, refetchInterval: 4000 })
  const [text, setText] = useState('')
  const [viewer, setViewer] = useState<number | null>(null)
  // Answering one message, or changing one's own: at most one at a time.
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null)
  const [editing, setEditing] = useState<ChatMessage | null>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLTextAreaElement>(null)

  const outbox = useSupportOutbox()
  const drafts = useDrafts()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  // Back leads where the chat was opened from; opened by a link, it leads home.
  const back = () => (canGoBack ? router.history.back() : void router.navigate({ to: '/' }))
  useEffect(() => {
    if (!canGoBack) void router.preloadRoute({ to: '/' })
  }, [])
  const edit = useMutation(
    orpc.support.edit.mutationOptions({
      onSuccess: (saved) =>
        queryClient.setQueryData(conversationQuery.queryKey, (prev) =>
          prev
            ? { ...prev, messages: prev.messages.map((m) => (m.id === saved.id ? saved : m)) }
            : prev,
        ),
    }),
  )
  const markRead = useMutation(
    orpc.support.markRead.mutationOptions({
      onSuccess: () => queryClient.setQueryData(unreadQuery.queryKey, { count: 0 }),
    }),
  )

  const server = conversation.data?.messages ?? []
  const refreshConversation = () =>
    queryClient.invalidateQueries({ queryKey: conversationQuery.queryKey })
  const feedback = useMutation(
    orpc.support.feedback.mutationOptions({ onSuccess: refreshConversation }),
  )
  const handoff = useMutation(
    orpc.support.handoff.mutationOptions({ onSuccess: refreshConversation }),
  )
  const handledByAi = conversation.data?.handler === 'ai'
  // Seen: whenever the newest answer from the team changes while the chat is open.
  const lastStaffId = [...server].reverse().find((m) => m.author === 'staff')?.id
  useEffect(() => {
    if (lastStaffId && document.visibilityState === 'visible') markRead.mutate({})
  }, [lastStaffId])

  const messages: ChatMessage[] = useMemo(
    () => [...server, ...outgoingMessages(outbox.items, 'user')],
    [server, outbox.items],
  )

  const stick = useStickToBottom(
    scroller,
    messages.map((m) => m.id),
  )

  const submit = () => {
    if (editing) {
      edit.mutate({ messageId: editing.id, body: text.trim() })
      setEditing(null)
    } else {
      outbox.send(text.trim(), drafts.take(), replyTo ? quoteOf(replyTo) : undefined)
      setReplyTo(null)
      stick.jump()
    }
    setText('')
  }
  const answer = (m: ChatMessage) => {
    if (editing) setText('')
    setEditing(null)
    setReplyTo(m)
    input.current?.focus()
  }
  const change = (m: ChatMessage) => {
    setReplyTo(null)
    setEditing(m)
    setText(m.body)
    input.current?.focus()
  }

  // Files still on their way have only a thumbnail to show.
  const media: ViewerItem[] = server.flatMap((m) =>
    m.attachments
      .filter((a) => a.status === 'ready')
      .map((a) => ({
        id: a.id,
        kind: a.kind,
        url: a.url,
        poster: a.posterUrl,
        who: m.author === 'user' ? 'Вы' : (m.staffName ?? 'Поддержка'),
        when: chatWhen(m.createdAt),
      })),
  )
  const empty = conversation.isSuccess && messages.length === 0

  return (
    <div className="mx-auto flex h-dvh min-h-0 w-full max-w-[720px] flex-col bg-bg md:border-line md:border-x">
      <header className="flex items-center gap-3 border-line border-b px-5 pt-[max(16px,var(--tg-viewport-content-safe-area-inset-top,0px))] pb-4">
        <button
          type="button"
          aria-label="Назад"
          onClick={back}
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-sunken"
        >
          <ArrowLeft className="size-5" aria-hidden="true" />
        </button>
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-yellow text-on-yellow">
          {handledByAi ? (
            <Sparkles className="size-5" aria-hidden="true" />
          ) : (
            <Headset className="size-5" aria-hidden="true" />
          )}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <h1 className="font-display font-semibold text-lg">Поддержка</h1>
          <p className="flex items-center gap-1.5 text-ink-2 text-xs">
            <span className="size-[7px] rounded-full bg-green" aria-hidden="true" />
            {handledByAi
              ? 'ИИ · Помощник Snitch'
              : conversation.data?.handoffReason
                ? 'Ожидаем оператора'
                : 'Поддержка · оператор'}
          </p>
        </div>
        {handledByAi ? (
          <button
            type="button"
            aria-label="Позвать оператора"
            disabled={handoff.isPending}
            onClick={() => handoff.mutate({})}
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-sunken disabled:opacity-50"
          >
            <Headset className="size-5" aria-hidden="true" />
          </button>
        ) : null}
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div ref={scroller} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-4 pb-5">
          {empty ? (
            <Welcome
              ai={handledByAi}
              onTopic={(topic) => {
                setText(`${topic}: `)
                input.current?.focus()
              }}
            />
          ) : (
            <div className="mt-auto">
              <ChatThread
                messages={messages}
                own="user"
                senderOf={(m) => ({ name: m.staffName ?? 'Поддержка', sub: 'поддержка Snitch' })}
                onOpenMedia={(id) => setViewer(media.findIndex((x) => x.id === id))}
                onRetry={(m) => m.clientId && outbox.retry(m.clientId)}
                onCancel={(m) => m.clientId && outbox.cancel(m.clientId)}
                onOpenLink={openExternalLink}
                onReply={answer}
                onEdit={change}
                afterMessage={(m) =>
                  m.author === 'staff' &&
                  m.body &&
                  (m.id === lastStaffId || Boolean(m.feedback && m.feedback !== 'unknown')) ? (
                    <SupportFeedback
                      message={m}
                      pending={feedback.isPending || handoff.isPending}
                      onChange={(outcome) =>
                        feedback.mutate({ messageId: m.id, outcome, note: '' })
                      }
                      onHandoff={handledByAi ? () => handoff.mutate({}) : undefined}
                    />
                  ) : null
                }
              />
            </div>
          )}
          {conversation.isError && !conversation.data ? (
            <p className="mt-auto py-6 text-center text-ink-3 text-sm">
              Не получилось загрузить переписку — проверьте интернет
            </p>
          ) : null}
          {conversation.data?.aiPending ? (
            <p role="status" className="flex items-center gap-2 py-4 text-ink-2 text-sm">
              <LoaderCircle className="size-4 motion-safe:animate-spin" aria-hidden="true" />
              Помощник отвечает…
            </p>
          ) : null}
          {feedback.isError || handoff.isError ? (
            <p role="alert" className="py-3 text-red text-sm">
              Не получилось отправить — попробуйте ещё раз.
            </p>
          ) : null}
        </div>
        <JumpToLatest {...stick} />
      </div>

      <div className="border-line border-t bg-bg px-4 pt-3 pb-[max(20px,env(safe-area-inset-bottom))]">
        <Composer
          value={text}
          onChange={setText}
          onSend={submit}
          drafts={drafts}
          inputRef={input}
          context={
            editing
              ? { kind: 'edit', who: 'Редактирование', text: editing.body }
              : replyTo
                ? {
                    kind: 'reply',
                    who: replyTo.author === 'user' ? 'Вы' : (replyTo.staffName ?? 'Поддержка'),
                    text: quoteOf(replyTo).text,
                  }
                : null
          }
          onCancelContext={() => {
            if (editing) setText('')
            setEditing(null)
            setReplyTo(null)
          }}
        />
      </div>

      <MediaViewer
        items={media}
        index={viewer !== null && viewer >= 0 ? viewer : null}
        onIndex={setViewer}
        onClose={() => setViewer(null)}
      />
    </div>
  )
}

function Welcome({ onTopic, ai = false }: { onTopic: (topic: string) => void; ai?: boolean }) {
  return (
    <div className="mt-auto flex flex-col gap-3.5">
      <section className="flex flex-col gap-2.5 rounded-3xl bg-card p-5">
        <span className="flex size-11 items-center justify-center rounded-full bg-yellow text-on-yellow">
          <Headset className="size-5" aria-hidden="true" />
        </span>
        <h2 className="font-bold font-display text-xl tracking-[-0.6px]">
          {ai ? 'Поможем разобраться' : 'Здесь отвечают люди'}
        </h2>
        <p className="text-[15px] text-ink-2 leading-[1.45]">
          {ai
            ? 'Напишите, что случилось — сначала ответит Помощник Snitch. Если понадобится, подключим оператора. Можно приложить скриншот.'
            : 'Напишите, что случилось — ответит кто-то из команды Snitch. Скриншот или короткое видео помогут разобраться быстрее.'}
        </p>
      </section>
      <div className="flex flex-col gap-2">
        <p className="font-medium text-[13px] text-ink-3">Или выберите тему</p>
        <div className="flex flex-wrap gap-2">
          {TOPICS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onTopic(t)}
              className="h-10 rounded-full border-[1.5px] border-ink px-4 font-semibold text-sm"
            >
              {t}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
